import React, { useState, useEffect } from 'react';
import { 
  Save, Plus, Trash2, GripVertical, Loader2, Link as LinkIcon, 
  Eye, Facebook, Instagram, Twitter, Phone, Mail, MessageCircle,
  Columns, Type, Palette, RotateCcw, Heart, Home, Info, HelpCircle,
  Shield, FileText, CreditCard, Crown, Settings, User, Star, Gift,
  Calendar, MapPin, Image, Users, Search, Bookmark, Bell, HandHeart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Available icons for footer links
const ICON_OPTIONS = [
  { value: 'none', label: 'No Icon', icon: null },
  { value: 'Link', label: 'Link', icon: LinkIcon },
  { value: 'Home', label: 'Home', icon: Home },
  { value: 'Info', label: 'Info', icon: Info },
  { value: 'HelpCircle', label: 'Help', icon: HelpCircle },
  { value: 'Shield', label: 'Shield', icon: Shield },
  { value: 'FileText', label: 'Document', icon: FileText },
  { value: 'Mail', label: 'Mail', icon: Mail },
  { value: 'Phone', label: 'Phone', icon: Phone },
  { value: 'CreditCard', label: 'Credit Card', icon: CreditCard },
  { value: 'Crown', label: 'Crown', icon: Crown },
  { value: 'Heart', label: 'Heart', icon: Heart },
  { value: 'Star', label: 'Star', icon: Star },
  { value: 'Gift', label: 'Gift', icon: Gift },
  { value: 'User', label: 'User', icon: User },
  { value: 'Users', label: 'Users', icon: Users },
  { value: 'Settings', label: 'Settings', icon: Settings },
  { value: 'Search', label: 'Search', icon: Search },
  { value: 'Bookmark', label: 'Bookmark', icon: Bookmark },
  { value: 'Bell', label: 'Bell', icon: Bell },
  { value: 'Calendar', label: 'Calendar', icon: Calendar },
  { value: 'MapPin', label: 'Location', icon: MapPin },
  { value: 'Image', label: 'Image', icon: Image },
  { value: 'HandHeart', label: 'Donate', icon: HandHeart },
  { value: 'MessageCircle', label: 'Chat', icon: MessageCircle },
];

const getIconComponent = (iconName) => {
  if (!iconName || iconName === 'none') return null;
  const iconOption = ICON_OPTIONS.find(opt => opt.value === iconName);
  return iconOption?.icon || null;
};

const DEFAULT_FOOTER = {
  columns: [
    {
      id: 'quick_links',
      title: 'Quick Links',
      titleColor: '#ffffff',
      linkColor: '#9ca3af',
      order: 0,
      links: [
        { id: 'about', label: 'About Us', url: '/about', icon: 'Info', iconColor: '#10b981', order: 0 },
        { id: 'pricing', label: 'Pricing', url: '/pricing', icon: 'CreditCard', iconColor: '#10b981', order: 1 },
        { id: 'help', label: 'Help Center', url: '/help', icon: 'HelpCircle', iconColor: '#10b981', order: 2 },
      ]
    },
    {
      id: 'legal',
      title: 'Legal',
      titleColor: '#ffffff',
      linkColor: '#9ca3af',
      order: 1,
      links: [
        { id: 'privacy', label: 'Privacy Policy', url: '/privacy', icon: 'Shield', iconColor: '#10b981', order: 0 },
        { id: 'terms', label: 'Terms of Service', url: '/terms', icon: 'FileText', iconColor: '#10b981', order: 1 },
        { id: 'contact', label: 'Contact Us', url: '/contact', icon: 'Mail', iconColor: '#10b981', order: 2 },
      ]
    }
  ],
  copyright_text: '© 2025 Qbool. All rights reserved.',
  show_social_links: true,
  show_contact_info: true,
  background_color: '#1f2937',
  text_color: '#ffffff',
  icon_color: '#10b981'
};

const AdminFooterSettings = () => {
  const [settings, setSettings] = useState(DEFAULT_FOOTER);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [siteSettings, setSiteSettings] = useState({});
  const { toast } = useToast();

  useEffect(() => {
    loadSettings();
    loadSiteSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/footer-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings({ ...DEFAULT_FOOTER, ...data });
      }
    } catch (error) {
      console.error('Failed to load footer settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSiteSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/site-settings/public`);
      if (response.ok) {
        const data = await response.json();
        setSiteSettings(data);
      }
    } catch (error) {
      console.error('Failed to load site settings:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/footer-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast({
          title: "Saved!",
          description: "Footer settings updated successfully.",
        });
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save footer settings",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(DEFAULT_FOOTER);
    toast({
      title: "Reset",
      description: "Footer reset to default. Click Save to apply.",
    });
  };

  const addColumn = () => {
    const newColumn = {
      id: `col_${Date.now()}`,
      title: 'New Column',
      titleColor: settings.text_color || '#ffffff',
      linkColor: '#9ca3af',
      order: settings.columns.length,
      links: []
    };
    setSettings({ ...settings, columns: [...settings.columns, newColumn] });
  };

  const removeColumn = (columnId) => {
    setSettings({
      ...settings,
      columns: settings.columns.filter(c => c.id !== columnId)
    });
  };

  const updateColumn = (columnId, field, value) => {
    setSettings({
      ...settings,
      columns: settings.columns.map(c =>
        c.id === columnId ? { ...c, [field]: value } : c
      )
    });
  };

  const addLink = (columnId) => {
    const column = settings.columns.find(c => c.id === columnId);
    const newLink = {
      id: `link_${Date.now()}`,
      label: 'New Link',
      url: '/',
      icon: 'Link',
      iconColor: settings.icon_color || '#10b981',
      order: column.links.length
    };
    updateColumn(columnId, 'links', [...column.links, newLink]);
  };

  const removeLink = (columnId, linkId) => {
    const column = settings.columns.find(c => c.id === columnId);
    updateColumn(columnId, 'links', column.links.filter(l => l.id !== linkId));
  };

  const updateLink = (columnId, linkId, field, value) => {
    const column = settings.columns.find(c => c.id === columnId);
    const updatedLinks = column.links.map(l =>
      l.id === linkId ? { ...l, [field]: value } : l
    );
    updateColumn(columnId, 'links', updatedLinks);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-mint-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Footer Settings</h1>
          <p className="text-gray-600">Customize your website footer</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset}>
            <RotateCcw className="w-4 h-4 mr-2" /> Reset
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-mint-500 hover:bg-mint-600">
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Save Changes</>
            )}
          </Button>
        </div>
      </div>

      {/* Footer Columns */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Columns className="w-5 h-5" />
              Footer Columns
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">Add columns with links</p>
          </div>
          <Button variant="outline" size="sm" onClick={addColumn}>
            <Plus className="w-4 h-4 mr-1" /> Add Column
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {settings.columns.map((column) => (
              <div key={column.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Input
                    value={column.title}
                    onChange={(e) => updateColumn(column.id, 'title', e.target.value)}
                    placeholder="Column Title"
                    className="font-semibold"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeColumn(column.id)}
                    className="text-red-500 hover:text-red-700 ml-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                {/* Column Colors */}
                <div className="flex items-center gap-4 p-2 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-500">Title:</Label>
                    <input
                      type="color"
                      value={column.titleColor || settings.text_color || '#ffffff'}
                      onChange={(e) => updateColumn(column.id, 'titleColor', e.target.value)}
                      className="w-6 h-6 rounded cursor-pointer border-0 p-0"
                      title="Title Color"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-500">Links:</Label>
                    <input
                      type="color"
                      value={column.linkColor || '#9ca3af'}
                      onChange={(e) => updateColumn(column.id, 'linkColor', e.target.value)}
                      className="w-6 h-6 rounded cursor-pointer border-0 p-0"
                      title="Link Color"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  {column.links.map((link) => {
                    const IconComponent = getIconComponent(link.icon);
                    return (
                      <div key={link.id} className="p-2 bg-gray-50 rounded-lg space-y-2">
                        <div className="flex items-center gap-2">
                          <GripVertical className="w-4 h-4 text-gray-300 flex-shrink-0" />
                          {IconComponent && (
                            <div className="w-6 h-6 bg-white rounded flex items-center justify-center flex-shrink-0">
                              <IconComponent className="w-3 h-3 text-gray-600" />
                            </div>
                          )}
                          <Input
                            value={link.label}
                            onChange={(e) => updateLink(column.id, link.id, 'label', e.target.value)}
                            placeholder="Label"
                            className="text-sm h-8"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLink(column.id, link.id)}
                            className="text-red-500 p-1 flex-shrink-0"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-2 pl-6">
                          <Input
                            value={link.url}
                            onChange={(e) => updateLink(column.id, link.id, 'url', e.target.value)}
                            placeholder="/url"
                            className="text-sm h-8 flex-1"
                          />
                          <Select
                            value={link.icon || 'none'}
                            onValueChange={(value) => updateLink(column.id, link.id, 'icon', value)}
                          >
                            <SelectTrigger className="h-8 w-28">
                              <SelectValue placeholder="Icon" />
                            </SelectTrigger>
                            <SelectContent>
                              {ICON_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  <div className="flex items-center gap-2">
                                    {opt.icon && <opt.icon className="w-3 h-3" />}
                                    <span className="text-xs">{opt.label}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <input
                            type="color"
                            value={link.iconColor || settings.icon_color || '#10b981'}
                            onChange={(e) => updateLink(column.id, link.id, 'iconColor', e.target.value)}
                            className="w-8 h-8 rounded cursor-pointer border-0 p-0"
                            title="Icon Color"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => addLink(column.id)}
                  className="w-full text-mint-600 hover:text-mint-700"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Link
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Display Options */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Display Options
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="flex gap-1">
                <Facebook className="w-4 h-4 text-blue-600" />
                <Instagram className="w-4 h-4 text-pink-600" />
                <Twitter className="w-4 h-4 text-sky-500" />
              </div>
              <div>
                <p className="font-medium">Show Social Links</p>
                <p className="text-xs text-gray-500">Display social media icons from Site Settings</p>
              </div>
            </div>
            <Switch
              checked={settings.show_social_links}
              onCheckedChange={(checked) => setSettings({ ...settings, show_social_links: checked })}
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="flex gap-1">
                <Mail className="w-4 h-4 text-gray-600" />
                <Phone className="w-4 h-4 text-gray-600" />
                <MessageCircle className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="font-medium">Show Contact Info</p>
                <p className="text-xs text-gray-500">Display contact details from Site Settings</p>
              </div>
            </div>
            <Switch
              checked={settings.show_contact_info}
              onCheckedChange={(checked) => setSettings({ ...settings, show_contact_info: checked })}
            />
          </div>
        </CardContent>
      </Card>

      {/* Copyright & Content */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Type className="w-5 h-5" />
            Copyright Text
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Copyright Text</Label>
            <Input
              value={settings.copyright_text}
              onChange={(e) => setSettings({ ...settings, copyright_text: e.target.value })}
              placeholder="© 2025 Your Company. All rights reserved."
            />
          </div>
          
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> Footer colors (background, text) are controlled from the <a href="/admin/website-designer" className="underline font-medium">Website Designer</a>. 
              This page is only for managing footer links and content.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader>
          <CardTitle>Footer Preview</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-hidden rounded-b-lg">
          <div 
            className="p-8"
            style={{ backgroundColor: settings.background_color, color: settings.text_color }}
          >
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
                {/* Logo/Brand Column */}
                <div>
                  <h3 className="text-lg font-bold mb-3">qbool</h3>
                  <p className="text-sm opacity-80">
                    A trusted Islamic matrimonial service connecting Muslims worldwide.
                  </p>
                  {settings.show_social_links && (
                    <div className="flex gap-3 mt-4">
                      {siteSettings.facebook_url && <Facebook className="w-5 h-5 opacity-70 hover:opacity-100 cursor-pointer" />}
                      {siteSettings.instagram_url && <Instagram className="w-5 h-5 opacity-70 hover:opacity-100 cursor-pointer" />}
                      {siteSettings.twitter_url && <Twitter className="w-5 h-5 opacity-70 hover:opacity-100 cursor-pointer" />}
                      {!siteSettings.facebook_url && !siteSettings.instagram_url && !siteSettings.twitter_url && (
                        <>
                          <Facebook className="w-5 h-5 opacity-50" />
                          <Instagram className="w-5 h-5 opacity-50" />
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Dynamic Columns */}
                {settings.columns.map((column) => (
                  <div key={column.id}>
                    <h4 className="font-semibold mb-3">{column.title}</h4>
                    <ul className="space-y-2 text-sm">
                      {column.links.map((link) => (
                        <li key={link.id}>
                          <span className="opacity-70 hover:opacity-100 cursor-pointer">{link.label}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Contact Info */}
              {settings.show_contact_info && (siteSettings.contact_email || siteSettings.contact_phone) && (
                <div className="flex flex-wrap gap-4 text-sm opacity-70 mb-4 pt-4 border-t border-white/20">
                  {siteSettings.contact_email && (
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" /> {siteSettings.contact_email}
                    </span>
                  )}
                  {siteSettings.contact_phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" /> {siteSettings.contact_phone}
                    </span>
                  )}
                </div>
              )}

              {/* Copyright */}
              <div className="text-center text-sm opacity-60 pt-4 border-t border-white/20">
                {settings.copyright_text}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminFooterSettings;
